import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Injector, ViewChild } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha';
import {
  ButtonsContainerComponent,
  FormHeaderComponent,
} from 'qflow-form-renderer';
import { LocalizePipe } from '../../../core/pipes/localization.pipe';
import {
  AttachmentDataInputDto,
  AttachmentsClient,
  CustomFormOutputDto,
  DataDomainClient,
  FlowClient,
  FlowDataOutputDto,
  FlowRoleOutputDto,
  RoleClient,
  SessionClient,
  StartFlowAsGuestInputDto,
  StartFlowInputDto,
  StartFlowRenderInfoOutputDto,
  StartFlowResultOutputDto,
  TaskClient,
} from '../../../core/remote-clients/remote-client';
import { RedirectService } from '../../../core/services/redirect.service';
import { SessionService } from '../../../core/services/session.service';
import { AttachmentService } from '../../../services/attachments/attachment.service';
import { ComponentDataService } from '../../../services/component-data.service';
import { RemoteServiceBase } from '../../../services/remote/remote-base.service';
import { RemoteDataDomainService } from '../../../services/remote/remote-data-domain.service';
import { RemoteFlowService } from '../../../services/remote/remote-flow.service';
import { RemoteRoleService } from '../../../services/remote/remote-role.service';
import { FormRenderComponent } from '../form-render/form-render.component';
import { InteractiveStepBaseComponent } from '../interactive-step-base/interactive-step-base.component';

const UNEXPECTED_ERROR_LOADING_NEW_FLOW_FORM_MESSAGE =
  'UnexpectedErrorLoadingNewFlowForm';
const UNEXPECTED_ERROR_STARTING_FLOW_MESSAGE = 'UnexpectedErrorStartingFlow';

@Component({
  selector: 'app-start-flow',
  imports: [
    CommonModule,
    LocalizePipe,
    FormsModule,
    ReactiveFormsModule,
    FormHeaderComponent,
    FormRenderComponent,
    ButtonsContainerComponent,
    RecaptchaModule,
  ],
  providers: [
    FlowClient,
    AttachmentService,
    ComponentDataService,
    AttachmentsClient,
    SessionClient,
    SessionService,
    RedirectService,
    RemoteServiceBase,
    RemoteFlowService,
    RemoteRoleService,
    RemoteDataDomainService,
    DataDomainClient,
    RoleClient,
    FlowClient,
    TaskClient,
  ],
  templateUrl: './start-flow.component.html',
  styleUrl: './start-flow.component.scss',
})
export class StartFlowComponent
  extends InteractiveStepBaseComponent
  implements AfterViewInit
{
  public static readonly StartEventStepId =
    '00000000-0000-0000-0000-000000000000';

  templateId: string;
  startFlowForm?: CustomFormOutputDto;
  startFlowInfo?: StartFlowRenderInfoOutputDto;
  flowName: FormControl;
  flowDescription: string = '';
  flowData: FlowDataOutputDto[] = [];
  flowRoles: FlowRoleOutputDto[] = [];
  attachments?: AttachmentDataInputDto[];
  headerSubtitle: string = '';
  captchaToken: string = '';
  guestTimeZone: string = '';

  @ViewChild('captchaRef', { static: false }) captchaRef!: RecaptchaComponent;

  constructor(
    injector: Injector,
    private readonly attachmentService: AttachmentService
  ) {
    super(injector);
    this.templateId =
      this._route.snapshot.queryParamMap.get('templateId') ?? '';
    this.flowName = new FormControl('', [Validators.required]);
  }

  async ngAfterViewInit(): Promise<void> {
    this.showLoader();
    if (!this.isValidGuid(this.templateId)) {
      return this.redirectToErrorPage(this.localize('TemplateNotFound'));
    }
    await this.loadFlowRenderInfo();

    this.hideLoader();
    if (this.startFlowInfo?.templateName) {
      this.updateTitle(this.startFlowInfo.templateName);
    }
  }

  protected override updateTitle(childTitle: string) {
    const localizedTitle = this._localizationService.localize(
      'NewProcessTitle',
      childTitle
    );

    super.updateTitle(localizedTitle);
  }

  private async loadFlowRenderInfo(): Promise<void> {
    try {
      const newFlowInfo = await this._flowService.getNewFlowInfo(
        this.templateId
      );
      this.initializeFlowData(newFlowInfo);
      await this.processFormData();
    } catch (error) {
      this.handleException(
        error,
        UNEXPECTED_ERROR_LOADING_NEW_FLOW_FORM_MESSAGE
      );
    }
  }

  private initializeFlowData(flowInfo: StartFlowRenderInfoOutputDto): void {
    this.headerSubtitle = `${flowInfo.templateName} (${flowInfo.versionName})`;
    this.startFlowInfo = flowInfo;
    this.startFlowForm = flowInfo.customForm;
    this.versionId = flowInfo.versionId!;
    this.templateId = flowInfo.templateId!;
    this.flowData = flowInfo.flowDatas!;
    this.flowRoles = flowInfo.flowRoles!;
    this.commentsScope = flowInfo.scope?.commentScope;
    this.attachmentsScope = flowInfo.scope?.attachmentsScope;
  }

  private async processFormData(): Promise<void> {
    const formJson = JSON.parse(this.startFlowForm?.content ?? '');
    this.qformCard.parse(formJson);
    this.initDependencies(formJson, this.flowData);

    this.initData(this.flowData);
    await this.fillListDataOptions(this.flowData);
    await this.fillDataControlWithValueAndScope(this.flowData);
    await this.fillDependentListDataOptions(this.flowData);
    this.guestTimeZone = this._cultureService.currentTimeZone;
    this.fillFileDataWithScope();
    this.fillRolesWithScope(this.flowRoles);

    if (this.qformCard.getCommentLog()) {
      this.initCommentLog([]);
    }

    this.renderQformCard();
  }

  async startFlow(): Promise<void> {
    if (!this.isFlowNameValid()) {
      return this.showFlowNameError();
    }

    try {
      this.validateForm(this.commentsScope?.required || false);

      this.showLoader();
      await this.handleAttachments();

      const result = await this._flowService.start(
        this.buildStartFlowInputDto()
      );

      this.redirectToFlowStartedView(result);
    } catch (error) {
      this.handleException(error, UNEXPECTED_ERROR_STARTING_FLOW_MESSAGE);
    } finally {
      this.resetCaptcha();
      this.attachments = [];
    }
  }

  private async handleAttachments(): Promise<void> {
    this.attachments = await this.attachmentService.uploadTempFiles(
      this.qformCard,
      this.startFlowForm?.templateVersionId ?? ''
    );
  }

  private isFlowNameValid(): boolean {
    return this.startFlowInfo?.autoGenerateName || this.flowName.valid;
  }

  private showFlowNameError(): void {
    this.flowName.markAsTouched();
    this.toastrService.error(this.localize('FlowNameRequired'));
  }

  private buildStartFlowInputDto():
    | StartFlowInputDto
    | StartFlowAsGuestInputDto {
    const dataValues = this.getDataValues();
    const roleMembers = this.getRoleMembers();
    const comment = this.getComment() ?? '';

    const data = {
      templateId: this.templateId,
      versionId: this.versionId,
      flowName: this.flowName.value,
      flowDescription: this.flowDescription,
      dataIdsWithValues: dataValues,
      roles: roleMembers,
      attachments: this.attachments,
      comment: comment,
    };

    if (this.isGuest) {
      return new StartFlowAsGuestInputDto({
        ...data,
        captchaToken: this.captchaToken,
      });
    } else {
      return new StartFlowInputDto(data);
    }
  }

  private localize(key: string): string {
    return this._localizationService.localize(key);
  }

  redirectToFlowStartedView(result: StartFlowResultOutputDto) {
    if (!this.isInIframe()) {
      return;
    }

    this._redirectService.redirectToFlowStarted(result);
  }

  captchaResolved(event: string | null) {
    this.captchaToken = event || '';
  }

  private resetCaptcha() {
    if (this.isGuest && this.captchaRef) {
      this.captchaRef.reset();
    }
  }

  protected override getCurrentStepId(): string | undefined {
    return StartFlowComponent.StartEventStepId;
  }
}
